/**
 * T-4 — the COVERAGE path's per-filing chain.
 *
 * The broad-scan path has `announcement_scans` and the triage console. The
 * coverage path has neither: `is_fast_lane` groups make the broad scan SKIP
 * their members, so a paying customer's filings never appear in the surface
 * built to show what happened to a filing. The least observable filings in the
 * system are the ones that are paid for — which is why the 2026-08-27 AQN
 * investigation had to be done in SQL and Cloud Logging rather than here.
 *
 * One row per filing, showing the whole chain in the order it happened:
 *   classify → route (T-1) → plan (T-2) → generate → live
 *
 * Deliberately shows BOTH sector axes. Their divergence — commodity says
 * Industrials, entity says Resources — is what caused the AQN miss, and it is
 * invisible unless the two are put side by side.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  getCoverageChain,
  type CoverageChainResponse,
  type CoverageChainRow,
} from '../lib/api/triage.js';

const OUTCOME_STYLE: Record<string, { label: string; cls: string; hint: string }> = {
  complete: {
    label: 'Complete', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    hint: 'Live on every active masthead.',
  },
  partial: {
    label: 'Partial', cls: 'bg-amber-50 text-amber-800 border-amber-200',
    hint: 'Some mastheads live, others not. For a coverage customer this is a P1 breach.',
  },
  no_article: {
    label: 'No article', cls: 'bg-rose-50 text-rose-700 border-rose-200',
    hint: 'Nothing generated. Check the routing decision — this is the AQN Bell Wire shape.',
  },
  declined: {
    label: 'Declined', cls: 'bg-slate-100 text-slate-600 border-slate-200',
    hint: 'The planner deliberately declined this filing, and recorded why.',
  },
};

function Axis({ label, value, diverged }: { label: string; value: string | null; diverged: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 ${diverged ? 'text-amber-700 font-medium' : 'text-slate-500'}`}>
      <span className="text-[10px] uppercase tracking-wide">{label}</span>
      <span>{value ?? '—'}</span>
    </span>
  );
}

function ChainRow({ row }: { row: CoverageChainRow }) {
  const style = OUTCOME_STYLE[row.outcome] ?? OUTCOME_STYLE.no_article;
  // The CT-3 divergence, made visible. Commodity is a property of the FILING;
  // entity sector is what the COMPANY is. When they disagree, routing on the
  // wrong one is how coverage becomes a lottery.
  const diverged = Boolean(
    row.entity_sector && row.announcement_sector &&
    row.entity_sector !== row.announcement_sector,
  );

  return (
    <tr className="border-b border-slate-100 align-top">
      <td className="py-2 pr-3">
        <div className="font-medium text-slate-800">{row.company_code}</div>
        <div className="text-xs text-slate-500">{row.coverage_group}</div>
      </td>

      <td className="py-2 pr-3 max-w-md">
        <div className="text-slate-700">{row.announcement_title}</div>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs">
          <Axis label="commodity" value={row.announcement_sector} diverged={diverged} />
          <Axis label="entity" value={row.entity_sector} diverged={diverged} />
          {row.editorial_category && (
            <span className="text-slate-500">{row.editorial_category}</span>
          )}
          {row.price_sensitive && (
            <span className="text-rose-600 font-medium">price-sensitive</span>
          )}
        </div>
      </td>

      {/* Route — T-1. Null for filings predating migration 326. */}
      <td className="py-2 pr-3 text-xs text-slate-600 whitespace-nowrap">
        {row.matched_count === null ? (
          <span className="text-slate-400">not recorded</span>
        ) : (
          <>
            <div>{row.matched_count} of {row.rules_evaluated} rules</div>
            {(row.targets_matched?.length ?? 0) > 0 && (
              <div className="text-slate-500">{row.targets_matched!.length} masthead(s)</div>
            )}
            {row.backstop_applied && (
              <div className="text-amber-700 font-medium" title="The rules left a masthead empty for a coverage customer and G-1 forced the Coverage Article in. Should be rare.">
                backstop fired
              </div>
            )}
          </>
        )}
      </td>

      {/* Plan — T-2. A decline is now a RECORD, not an absence. */}
      <td className="py-2 pr-3 text-xs">
        {row.skip_reason ? (
          <div>
            <div className="font-medium text-slate-700">{row.skip_reason}</div>
            {row.skip_detail && <div className="text-slate-500">{row.skip_detail}</div>}
            {row.superseded_by_announcement_id && (
              <div className="text-slate-500">folded into another filing</div>
            )}
          </div>
        ) : (
          <span className="text-slate-400">planned</span>
        )}
      </td>

      {/* Generate + live. `is_live` means main, not test. */}
      <td className="py-2 pr-3 text-xs">
        {row.articles.length === 0 ? (
          <span className="text-slate-400">none</span>
        ) : (
          <ul className="space-y-0.5">
            {row.articles.map((a) => (
              <li key={a.article_id} className="whitespace-nowrap">
                <span className={a.is_live ? 'text-emerald-700' : 'text-amber-700'}>
                  {a.is_live ? '● live' : '○ test'}
                </span>{' '}
                <span className="text-slate-700">{a.target_name}</span>
              </li>
            ))}
          </ul>
        )}
      </td>

      <td className="py-2">
        <span
          title={style.hint}
          className={`inline-block rounded border px-2 py-0.5 text-xs ${style.cls}`}
        >
          {style.label}
        </span>
        <div className="mt-1 text-xs text-slate-500">
          {row.live_mastheads}/{row.active_mastheads} live
        </div>
      </td>
    </tr>
  );
}

export function CoverageChainPanel() {
  const [data, setData] = useState<CoverageChainResponse | null>(null);
  const [days, setDays] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (d: number) => {
    setLoading(true);
    setError(null);
    try {
      setData(await getCoverageChain({ days: d, limit: 300 }));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load the coverage chain');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  return (
    <section className="mt-8">
      <header className="flex items-baseline justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Coverage path</h2>
          <p className="text-sm text-slate-500">
            Fast-lane filings skip the broad scan, so they never appear in the
            triage table above. This is the same chain for them:
            classify → route → plan → generate → live.
          </p>
        </div>
        <label className="text-sm text-slate-600 whitespace-nowrap">
          Days{' '}
          <select
            className="rounded border border-slate-300 px-2 py-1"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {[1, 3, 7, 14].map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
      </header>

      {data && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {Object.entries(data.outcomes).map(([outcome, n]) => {
            const st = OUTCOME_STYLE[outcome] ?? OUTCOME_STYLE.no_article;
            return (
              <span key={outcome} title={st.hint}
                    className={`rounded border px-2 py-0.5 ${st.cls}`}>
                {st.label}: {n}
              </span>
            );
          })}
        </div>
      )}

      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      {loading && <p className="mt-3 text-sm text-slate-500">Loading…</p>}

      {data && !loading && (
        data.items.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">
            No coverage-path filings in this window.
          </p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3">Company</th>
                  <th className="py-2 pr-3">Filing &amp; classification</th>
                  <th className="py-2 pr-3">Route</th>
                  <th className="py-2 pr-3">Plan</th>
                  <th className="py-2 pr-3">Articles</th>
                  <th className="py-2">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <ChainRow key={row.announcement_id} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
}
